import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { History as HistoryIcon, Truck, Warehouse, PackageCheck, AlertCircle } from "lucide-react";

const META = {
  PICKED_UP_BY_RIDER: { icon: Truck, color: "text-blue-400", label: "Picked Up at Store" },
  RECEIVED_AT_WAREHOUSE: { icon: Warehouse, color: "text-indigo-400", label: "Dropped at Warehouse" },
  OUT_FOR_LAST_MILE: { icon: Truck, color: "text-teal-400", label: "Last-Mile Pickup" },
  DELIVERED: { icon: PackageCheck, color: "text-green-400", label: "Delivered" },
};

export default function RiderHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/rider/history")
      .then(r => setRows(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="rider-history-page" className="space-y-4">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-1">/ History</div>
        <h1 className="font-display text-2xl font-bold tracking-tight">My History</h1>
        <p className="text-slate-600 dark:text-slate-400 text-sm">{loading ? "Loading…" : `${rows.length} action${rows.length === 1 ? "" : "s"}`}</p>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-14 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <HistoryIcon className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display text-base text-slate-700 dark:text-slate-300 mb-1">No history yet</div>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((h, idx) => {
            const meta = META[h.status] || { icon: AlertCircle, color: "text-slate-600 dark:text-slate-400", label: h.status };
            const Icon = meta.icon;
            return (
              <div
                key={h.id}
                data-testid={`rider-hist-${idx}`}
                className="border border-slate-200 dark:border-slate-800 rounded-lg p-3 bg-slate-100/40 dark:bg-slate-900/40 flex items-start gap-3"
              >
                <div className={`w-8 h-8 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 grid place-items-center ${meta.color} shrink-0`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 dark:text-white">{meta.label}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 font-mono-tech truncate">
                    {h.qr_code_id || "—"}{h.package_id ? ` · ${h.package_id}` : ""}
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 font-mono-tech text-right shrink-0">
                  {new Date(h.created_at).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
